"use strict"

import React from "react"

import { postFavouriteGraphSet, patchFavouriteSet, getObjectsList, addNewObject } from "../../Loaders"
import { logToServer } from "../../ErrorBoundary"
import { parseDate, sortDates } from "../utils"

export const FavouriteConfigContext = React.createContext({})

function updateFavouriteRunSet(id, name, runIDs, description) {
  return patchFavouriteSet(
    "update",
    id,
    {
      name,
      description,
      encoded: runIDs.map((rrid) => rrid.toString()).join(","),
    },
    "run",
  )
}

function updateFavouriteGraphSet(id, name, graphConfigs, description, view, interfaces) {
  return patchFavouriteSet(
    "update",
    id,
    {
      name,
      description,
      encoded: JSON.stringify(graphConfigs),
      view: view,
      interfaces: interfaces,
    },
    "graph",
  )
}

export function withFavouritesConfig(Component) {
  return function (props) {
    return (
      <FavouriteConfigContext.Consumer>
        {(configData) => <Component {...props} {...configData} />}
      </FavouriteConfigContext.Consumer>
    )
  }
}

/*
 * Container which fetches various bits of static data from the server and exposes them
 * via a React context.  Use withFavouritesConfig to access the value from child components.
 */

export default class FavouriteConfigProvider extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      favouriteGraphSets: [],
      favouriteRunSets: [],
      user_list: [],
      is_config_editor: false,
      editable_configs: [],
    }

    this.registerFavouriteGraphSet = this.registerFavouriteGraphSet.bind(this)
    this.registerFavouriteRunSet = this.registerFavouriteRunSet.bind(this)
    this.deleteFavouriteGraphSet = this.deleteFavouriteGraphSet.bind(this)
    this.deleteFavouriteRunSet = this.deleteFavouriteRunSet.bind(this)
    this.restoreFavouriteGraphSet = this.restoreFavouriteGraphSet.bind(this)
    this.restoreFavouriteRunSet = this.restoreFavouriteRunSet.bind(this)
    this.updateFavouriteGraphSet = this.updateFavouriteGraphSet.bind(this)
    this.updateFavouriteRunSet = this.updateFavouriteRunSet.bind(this)
    this.loadFavouriteGraphs = this.loadFavouriteGraphs.bind(this)
  }

  processFavouriteSeries(arr) {
    return sortDates(
      arr.map((entry) => ({
        ...entry,
        date_created: parseDate(entry.date_created),
        date_updated: parseDate(entry.date_updated),
      })),
      "date_updated",
      false,
    )
  }

  componentDidMount() {
    getObjectsList("/users/list/active/")
      .then((users) => {
        this.setState({
          user_list: users.users,
          is_config_editor: users.is_config_editor,
          editable_configs: users.editable_configs,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          userListErr: err,
        })
      })

    this.loadFavourites()
  }

  loadFavouriteGraphs() {
    // Load favorite graph sets
    getObjectsList("/users/favourites/graphlayouts/")
      .then((result) => {
        this.setState({
          favouriteGraphSets: this.processFavouriteSeries(
            result.map((favRecord) => ({
              ...favRecord,
              graphConfigs: this.getCleanGraphConfigs(JSON.parse(favRecord.encoded)),
            })),
          ),
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
      })
  }

  loadFavouriteRunSets() {
    // Load favorite run sets
    getObjectsList("/users/favourites/runreactors/")
      .then((result) => {
        this.setState({
          favouriteRunSets: this.processFavouriteSeries(
            result.map((favRecord) => ({
              ...favRecord,
              runs: favRecord.encoded.split(",").map((s) => parseInt(s)),
            })),
          ),
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
      })
  }

  loadFavourites() {
    this.loadFavouriteGraphs()
    this.loadFavouriteRunSets()
  }

  render() {
    return (
      <FavouriteConfigContext.Provider
        value={{
          user_list: this.state.user_list,
          is_config_editor: this.state.is_config_editor,
          editable_configs: this.state.editable_configs,

          favouriteGraphSets: this.state.favouriteGraphSets,
          favouriteRunSets: this.state.favouriteRunSets,

          registerFavouriteGraphSet: this.registerFavouriteGraphSet,
          registerFavouriteRunSet: this.registerFavouriteRunSet,
          deleteFavouriteGraphSet: this.deleteFavouriteGraphSet,
          deleteFavouriteRunSet: this.deleteFavouriteRunSet,
          restoreFavouriteGraphSet: this.restoreFavouriteGraphSet,
          restoreFavouriteRunSet: this.restoreFavouriteRunSet,
          updateFavouriteGraphSet: this.updateFavouriteGraphSet,
          updateFavouriteRunSet: this.updateFavouriteRunSet,
          loadFavouriteGraphs: this.loadFavouriteGraphs,
        }}
      >
        {this.props.children}
      </FavouriteConfigContext.Provider>
    )
  }

  getCleanGraphConfigs(graphConfigs) {
    return graphConfigs.map((g) => {
      if (g?.special === "multi") {
        // Removing specific run info from graph setting
        const { observations, ...info } = g
        return {
          ...info,
          observations: [],
        }
      } else if (["single", "several-tags"].includes(g?.special)) {
        // Deprecated 'special' types
        const { special, ...info } = g
        return info
      } else if (g?.special === "microbial-community") {
        // Removing specific microbe info from graph setting
        const { microbes, ...info } = g
        return {
          ...info,
          microbes: [],
        }
      } else {
        return g
      }
    })
  }

  registerFavouriteGraphSet(name, graphConfigs, description, view, replace, interfaces) {
    const cleanGraphConfigs = this.getCleanGraphConfigs(graphConfigs)

    if (replace) {
      return this.updateFavouriteGraphSet(replace, name, cleanGraphConfigs, description, view, interfaces)
    } else {
      return postFavouriteGraphSet(name, cleanGraphConfigs, description, view, interfaces).then((_) => {
        this.loadFavourites()
      })
    }
  }

  restoreFavouriteGraphSet(id, interfaces) {
    return patchFavouriteSet(
      "restore",
      id,
      {
        trashed: false,
        interfaces: interfaces,
      },
      "graph",
    ).then((_) => {
      this.loadFavourites()
    })
  }

  deleteFavouriteGraphSet(id) {
    return patchFavouriteSet(
      "delete",
      id,
      {
        trashed: true,
      },
      "graph",
    ).then((_) => {
      this.loadFavourites()
    })
  }

  updateFavouriteGraphSet(id, name, graphConfigs, description, view, interfaces = []) {
    return updateFavouriteGraphSet(id, name, graphConfigs, description, view, interfaces).then((_) => {
      this.loadFavourites()
    })
  }

  registerFavouriteRunSet(name, runIDs, description, replace) {
    if (replace) {
      return updateFavouriteRunSet(replace, name, runIDs, description).then((_) => {
        this.loadFavourites()
      })
    } else {
      return addNewObject("/users/favourites/runreactors/", {
        name: name,
        description: description,
        encoded: runIDs.map((id) => id.toString()).join(","),
      }).then((_) => {
        this.loadFavourites()
      })
    }
  }

  deleteFavouriteRunSet(id) {
    return patchFavouriteSet("delete", id, { trashed: true }, "run").then((_) => {
      this.loadFavourites()
    })
  }

  restoreFavouriteRunSet(id) {
    return patchFavouriteSet("restore", id, { trashed: false, method: "DELETE" }, "run").then((_) => {
      this.loadFavourites()
    })
  }

  updateFavouriteRunSet(id, name, runIDs, description) {
    return updateFavouriteRunSet(id, name, runIDs, description).then((_) => {
      this.loadFavourites()
    })
  }
}
